import { Injectable } from '@angular/core';
// tslint:disable:nx-enforce-module-boundaries
import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { ReplenishmentsService } from '@epicuro-next/data/cmi';
import { ReplenishmentDto } from '@epicuro-next/models/cmi';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectAccountId } from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  getReplenishments,
  getReplenishmentsFail,
  getReplenishmentsSuccess,
  uploadReplenishmentUpdates,
  uploadReplenishmentUpdatesFailure,
  uploadReplenishmentUpdatesSuccess,
} from '../actions';
import { OuterState } from '../reducers/replenishment.reducer';

@Injectable()
export class ReplenishmentsEffects {
  constructor(
    private replenishmentsService: ReplenishmentsService,
    private actions$: Actions,
    private store: Store<OuterState>,
  ) {}

  public showReplenishments$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PulseConfigurationKeys.CMIEnabled,
        PulseConfigurations
      >(
        PULSE_FEATURE_NAME,
        PulseConfigurationKeys.CMIEnabled,
        pulseConfigurationDefaultValues[PulseConfigurationKeys.CMIEnabled],
      ),
    ),
  );

  public getReplenishments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getReplenishments),
      withLatestFrom(this.showReplenishments$),
      filter(([, featureEnabled]) => !!featureEnabled),
      map(([data]) => data),
      switchMap(({ accountId }) =>
        this.replenishmentsService.getReplenishments(accountId).pipe(
          map((res: ReplenishmentDto) => getReplenishmentsSuccess(res)),
          catchError(() => [
            showSnackBarMessage({
              message: $localize`:replenishment|Replenishment get failed message@@replenishmentGetFailedMessage:Failed to get replenishments`,
              messageType: MessageType.WARNING,
            }),
            getReplenishmentsFail(),
          ]),
        ),
      ),
    ),
  );

  public updateReplenishment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadReplenishmentUpdates),
      concatLatestFrom(() => this.store.select(selectAccountId)),
      switchMap(([{ articles }, accountId]) =>
        this.replenishmentsService
          .patchReplenishment(accountId as string, articles)
          .pipe(
            switchMap((payload: ReplenishmentDto) => {
              return [
                uploadReplenishmentUpdatesSuccess(payload),
                showSnackBarMessage({
                  message: $localize`:replenishment|Replenishment update success message@@replenishmentUpdateSuccessMessage:Replenishments successfully updated`,
                  messageType: MessageType.SUCCESS,
                }),
              ];
            }),
            catchError(() => [
              showSnackBarMessage({
                message: $localize`:replenishment|Replenishment update failed message@@replenishmentUpdateFailedMessage:Failed to update replenishments`,
                messageType: MessageType.WARNING,
              }),
              uploadReplenishmentUpdatesFailure(),
            ]),
          ),
      ),
    ),
  );

  public fetchReplenishmentsOnAccountChange$ = createEffect(() =>
    this.store.pipe(
      select(selectAccountId),
      filter(isNonNullish),
      map((accountId: string) => getReplenishments({ accountId })),
    ),
  );
}
